// PLAN-judgment-cockpit Phase B (2026-05-15)
// Article H-channel feedback layer API client。worker-entry の /api/articles/:id/feedback* を呼ぶ。
//
// 対応 contract: spec/components/worker-entry/feedback-api.md
import { apiFetch } from './client'

export type FeedbackScope = 'version' | 'lineage'
export type FeedbackType =
  | 'like' | 'concern' | 'dislike' | 'comment'
  | 'reuse_ok' | 'reuse_ng'

export const VERSION_FEEDBACK_TYPES: FeedbackType[] = ['like', 'concern', 'dislike', 'comment']
export const LINEAGE_FEEDBACK_TYPES: FeedbackType[] = ['reuse_ok', 'reuse_ng']

export interface FeedbackItem {
  id: string
  article_id: string
  lineage_root_id: string
  decision_hash: string | null
  feedback_scope: FeedbackScope
  feedback_type: FeedbackType
  body: string | null
  reason_tag: string | null
  created_by: string
  created_at: string
  retracted_at: string | null
  retract_reason: string | null
}

export interface FeedbackSummary {
  version: {
    decision_hash: string | null
    like_count: number
    concern_count: number
    dislike_count: number
    comment_count: number
    last_at: string | null
  }
  lineage: {
    lineage_root_id: string
    reuse_state: 'reuse_ok' | 'reuse_ng' | null
    reuse_state_at: string | null
  }
  linked_observations: {
    count: number
    latest_entry_id: string | null
    latest_created_at: string | null
  }
}

export interface CreateFeedbackPayload {
  feedback_scope: FeedbackScope
  feedback_type: FeedbackType
  decision_hash?: string | null
  body?: string | null
  reason_tag?: string | null
}

// GET /api/articles/:id/feedback?scope=&include_retracted=
export async function listFeedback(
  articleId: string,
  opts: { scope?: FeedbackScope; includeRetracted?: boolean; limit?: number } = {},
  signal?: AbortSignal,
): Promise<{ article_id: string; items: FeedbackItem[] }> {
  return apiFetch(`/api/articles/${encodeURIComponent(articleId)}/feedback`, {
    query: {
      scope: opts.scope,
      include_retracted: opts.includeRetracted ? 'true' : undefined,
      limit: opts.limit,
    },
    signal,
  })
}

// POST /api/articles/:id/feedback
export async function createFeedback(
  articleId: string,
  payload: CreateFeedbackPayload,
  signal?: AbortSignal,
): Promise<FeedbackItem> {
  return apiFetch(`/api/articles/${encodeURIComponent(articleId)}/feedback`, {
    method: 'POST',
    body: payload,
    signal,
  })
}

// POST /api/articles/:id/feedback/:feedback_id/retract
export async function retractFeedback(
  articleId: string,
  feedbackId: string,
  retractReason?: string | null,
  signal?: AbortSignal,
): Promise<{ retracted: boolean; lineage_root_id: string | null }> {
  return apiFetch(`/api/articles/${encodeURIComponent(articleId)}/feedback/${encodeURIComponent(feedbackId)}/retract`, {
    method: 'POST',
    body: { retract_reason: retractReason ?? null },
    signal,
  })
}

// 表示用 helper: feedback_type の日本語ラベル
export const FEEDBACK_TYPE_LABELS: Record<FeedbackType, string> = {
  like: '👍 like',
  concern: '⚠️ concern',
  dislike: '👎 dislike',
  comment: '💬 comment',
  reuse_ok: '♻️ reuse ok',
  reuse_ng: '🚫 reuse ng',
}

// PLAN-judgment-cockpit Phase D (2026-05-15): Linked Observation 作成
// outcome (post-hoc fact) を Entry として記録する。kdb-mapping.md の SPEC に従い、
// source_article_ids に対象 Article id を入れることで linkage する。
export async function createLinkedObservation(
  articleId: string,
  payload: {
    title: string
    content: string
    one_line?: string
    subjectivity?: 'objective' | 'interpreted' | 'subjective'
  },
  signal?: AbortSignal,
): Promise<{ id: string }> {
  const { apiFetch } = await import('./client')
  return apiFetch('/api/entries', {
    method: 'POST',
    body: {
      title: payload.title,
      content: payload.content,
      abstract: payload.one_line ? { one_line: payload.one_line } : undefined,
      origin: 'human',
      subjectivity: payload.subjectivity ?? 'objective',
      visibility: 'private',
      source_article_ids: [articleId],
      source: { tool: 'manual', medium: 'direct' },
    },
    signal,
  })
}

// reason_tag controlled vocabulary
export const REASON_TAGS: { tag: string; scope: FeedbackScope; types: FeedbackType[]; label: string }[] = [
  { tag: 'self_like', scope: 'version', types: ['like'], label: '自分の流儀に合致' },
  { tag: 'self_dislike', scope: 'version', types: ['dislike'], label: '自分の流儀から外れる' },
  { tag: 'domain_mismatch', scope: 'version', types: ['concern','dislike'], label: 'domain 乖離' },
  { tag: 'outdated', scope: 'lineage', types: ['concern','reuse_ng'], label: '時間経過で陳腐化' },
  { tag: 'candidate_undervalued', scope: 'version', types: ['concern','dislike'], label: '候補を過小評価' },
  { tag: 'candidate_overvalued', scope: 'version', types: ['concern','dislike'], label: '候補を過大評価' },
  { tag: 'context_drift', scope: 'lineage', types: ['reuse_ng'], label: 'context 変化、再利用注意' },
  { tag: 'outcome_supports', scope: 'version', types: ['like'], label: '事後観察が支持' },
  { tag: 'outcome_refutes', scope: 'version', types: ['dislike','concern'], label: '事後観察が反証' },
  { tag: 'reuse_high_quality', scope: 'lineage', types: ['reuse_ok'], label: '高品質、積極利用' },
]
